import {
  CountChip,
  Icon,
  PermissionGate,
  Tooltip,
  useHasPermission,
  type IconName,
} from "@sarva/ui";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation, useMatch } from "react-router-dom";
import { NAV, type NavGroup, type NavLeaf } from "@/app/nav";
import { Brand } from "./Brand";

/**
 * doc 40 §8 — the application shell's left rail, and the thing the rejected
 * version most obviously lacked (doc 40 §16: "the shell *is* the product's
 * frame; leaving it unspecified is why the screens had no spine").
 *
 *   240px wide · bg-default · right border-default · 48px collapsed
 *   search box with a ⌘K hint at the top
 *   nav items: 8px-radius rows with a 16px outline icon
 *   collapsible groups with a chevron
 *   active item: bg-primary-weak + text-primary
 *   a "Collapse" control at the bottom
 */

/** Row geometry, shared by links, group headers and the collapse control, so
 * the three cannot drift apart by a pixel. (H) doc 40 §7: nav rows are
 * `radius-lg` (8px) — the one place 8px appears on something this small. */
const ROW =
  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium " +
  "transition-colors duration-hover ease-hover motion-reduce:transition-none " +
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "focus-visible:outline-strong";

/**
 * The collapsed rail's row: a square target, centred, with a bigger glyph.
 *
 * ── Why this is its own geometry rather than `ROW` minus the label ─────────
 *
 * It used to be exactly that, and the result measured **23×16px** — the label was
 * gone, so `gap-2` collapsed, `px-2` was all the width there was, and the row's
 * height became the icon's own 16px. Twenty of those at 18px apart read as a
 * column of specks rather than as navigation, and the target was a third of the
 * 44px a pointer wants.
 *
 * `size-10` (40×40) inside a 48px rail leaves 4px either side, which is the same
 * optical inset the expanded rail's `px-2` gives. `justify-center` and `p-0`
 * because a centred glyph in a square is the whole design at this width — there is
 * nothing to align to the left edge of.
 */
const ROW_COLLAPSED =
  "flex size-10 shrink-0 items-center justify-center rounded-lg p-0 " +
  "transition-colors duration-hover ease-hover motion-reduce:transition-none " +
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "focus-visible:outline-strong";

const ROW_IDLE = "text-secondary hover:bg-hover hover:text-default";
/** (H) doc 40 §8: the active item is `bg-primary-weak` + `text-primary`. */
const ROW_ACTIVE = "bg-primary-weak text-primary font-semibold";
/**
 * An unbuilt destination: visibly inert, and **not a link**.
 *
 * `text-disabled` is 2.48:1, which WCAG 1.4.3 exempts for an inactive control
 * — and this row genuinely is one. The icon drops to the same role so the row
 * reads as one greyed unit rather than a live glyph beside dead text.
 */
const ROW_UNBUILT = "text-disabled cursor-not-allowed";

/** The word used for an unbuilt screen, in one place so it reads identically. */
const NOT_YET = "not available yet";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Opens the ⌘K palette. Owned by the shell so the topnav can share it. */
  onOpenSearch: () => void;
  /** Live counts. In the real app these come from Query hooks (doc 27 §5). */
  counts?: Partial<Record<NonNullable<NavLeaf["badgeKey"]>, number>>;
}

function NavRow({
  item,
  collapsed,
  count,
}: {
  item: NavLeaf;
  collapsed: boolean;
  count?: number;
}) {
  // An ungated leaf spreads to no arguments, and `useHasPermission` is `every` over them —
  // so "no permission required" is already this helper's empty case rather than a new one.
  // Passing `undefined` straight through would ask the set whether it holds `undefined`,
  // which is `false`: the landing route would be denied to everybody.
  const allowed = useHasPermission(
    ...(item.permission === undefined ? [] : [item.permission]),
  );

  /**
   * (H) `isActive` is computed here, and **neither** `className` nor `children` is a
   * function. That is not a style preference — it is the fix for a bug that had made the
   * entire rail's styling inert.
   *
   * ── What react-router 7 does with `className={fn}` + `children={fn}` ──────────
   *
   * It does not resolve the function. It stringifies it. Every one of the sixteen nav
   * links carried this as its literal `class` attribute:
   *
   *     class="({ isActive }) => `${collapsed ? ROW_COLLAPSED : ROW} …`"
   *
   * plus react-router's own default `active` on the current one. So `ROW`, `ROW_ACTIVE`
   * and `ROW_IDLE` were never applied to a single link — the rows were unstyled inline
   * anchors, measured at **23×20px** with `display: inline`.
   *
   * It stayed invisible because the four *unbuilt* rows are `<span>`s with a plain string
   * className, so they looked right, and the expanded rail's rows still read as rows from
   * their icon and line-height alone. Collapsing the rail is what exposed it: with the
   * label gone there was nothing left to give the row a size, and the icons became the
   * specks in the 48px rail.
   *
   * `useMatch` with `end` mirrors what `NavLink` would have computed, so the active state
   * is identical — it is only the delivery that changed.
   *
   * Declared up here, beside `useHasPermission`, because the unbuilt branch below returns
   * early — a hook after it runs on some renders and not others. ESLint caught that the
   * moment it was written, which is the `react-hooks` rule — widened to cover plain
   * TypeScript files, not just `.tsx` — earning itself again (F27).
   */
  const isActive = useMatch({ path: item.to, end: item.to === "/" }) !== null;

  /**
   * An unbuilt screen is shown, and shown as unavailable.
   *
   * The first attempt at this filtered unbuilt leaves out of the rail
   * altogether, because they used to render "That page does not exist" — and
   * that traded one wrong impression for another: a product that looks like it
   * has four screens rather than one that is visibly part-built. `nav.ts`
   * already settles the same question for permissions ("anything a user might
   * reasonably expect access to stays visible and disabled"), and an unbuilt
   * screen is that case exactly. So the row stays, and what changes is that it
   * is **not an `<a>`**: there is nothing to navigate to, and a disabled
   * anchor is still an anchor to a keyboard and to a screen reader.
   *
   * It keeps `tabIndex={0}` on purpose. The explanation has to be *reachable*
   * — the queue screen's denied primary action is the precedent: an
   * explanation behind an unfocusable element is silent grey with extra
   * markup.
   */
  if (item.built === false) {
    if (!allowed && item.whenDenied === "hide") return null;
    const explanation = `${item.label} is ${NOT_YET}.`;
    return (
      <li>
        <Tooltip content={explanation} side="right">
          <span
            data-nav-unbuilt={item.to}
            aria-disabled="true"
            tabIndex={0}
            aria-label={`${item.label} — ${NOT_YET}`}
            className={`${collapsed ? ROW_COLLAPSED : ROW} ${ROW_UNBUILT}`}
          >
            <Icon name={item.icon} size={collapsed ? 5 : 4} className="text-disabled" />
            {collapsed ? null : (
              <>
                <span className="flex-1 truncate text-left">{item.label}</span>
                {/* The reason, in the rail rather than only in a tooltip: a
                    row that is merely grey says "you cannot", and the fact
                    here is "not yet", which is a different thing. */}
                <span className="shrink-0 text-2xs font-medium uppercase tracking-wide">
                  Soon
                </span>
              </>
            )}
          </span>
        </Tooltip>
      </li>
    );
  }

  const link = (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={`${collapsed ? ROW_COLLAPSED : ROW} ${isActive ? ROW_ACTIVE : ROW_IDLE}`}
      title={undefined}
    >
      {/* 20px collapsed, 16px expanded. At 48px wide the glyph *is* the row, so it
          carries the weight the label carries when there is one; beside a label, 20px
          would out-shout the text it belongs to. */}
      <Icon
        name={item.icon}
        size={collapsed ? 5 : 4}
        className={isActive ? "text-primary" : "text-tertiary"}
      />
      {collapsed ? null : (
        <>
          <span className="flex-1 truncate text-left">{item.label}</span>
          {count === undefined ? null : <CountChip>{count}</CountChip>}
        </>
      )}
    </NavLink>
  );

  // The tooltip is the collapsed rail's only label, so it is required there
  // and absent when the label is already on screen. `<Tooltip>` returns its
  // child bare when `content` is empty, which is what makes this one line.
  const wrapped = (
    <Tooltip content={collapsed ? item.label : undefined} side="right">
      {link}
    </Tooltip>
  );

  // `item.permission === undefined` is unreachable here — an ungated leaf is always
  // allowed above — but saying so is what lets the gate below take a defined permission.
  if (allowed || item.permission === undefined) return <li>{wrapped}</li>;
  if (item.whenDenied === "hide") return null;
  return (
    <li>
      <PermissionGate permission={item.permission}>{wrapped}</PermissionGate>
    </li>
  );
}

function NavGroupRow({
  group,
  collapsed,
  counts,
}: {
  group: NavGroup;
  collapsed: boolean;
  counts: SidebarProps["counts"];
}) {
  // ── A group holding a screen that exists opens by default. ───────────────
  //
  // `defaultOpen` alone left three built screens invisible on first load: the payer-rules
  // review queue (the product's one human-in-the-loop AI surface), fee schedules and
  // providers all sit in groups that started closed, so a user landing on the dashboard saw
  // neither them nor any hint they existed. Found by measurement rather than by looking —
  // an audit script walking `nav a[href=…]` could not find their links at all, which is
  // exactly what a new user experiences.
  //
  // Meanwhile the Deadlines group, whose two leaves are both `built: false`, opened on
  // nothing worth showing. So the rule is the one that matters: **a group opens if it
  // contains something you can actually use.** `defaultOpen` still wins where it is set
  // explicitly. See docs/30-design/42_DESIGN_AUDIT.md, D14.
  // ── …and then that rule made the rail taller than a 13" laptop ───────────
  //
  // Opening every group with a built child opened three of them at once — 11 child
  // rows — and the rail became 22 rows and ~850px. Measured at 1440×789 that hid
  // **222px**: `Patient balance`, `Reports`, `Scoreboard` and the whole Reference
  // data group were off the bottom, on every screen in the product. D14 had made
  // those screens reachable and this made six others unreachable-looking.
  //
  // So: the group holding the **current route** is open, and the others are closed.
  // One group at a time was 17 rows then; after D31 it is 10 closed and 21 at worst.
  //
  // This does not undo D14. Its failure was that a closed group gave a user no
  // reason to believe anything was inside it — three built screens were invisible
  // *and* unhinted. Every group header is still on screen with its chevron and its
  // label, which is a reason; and landing on `/payer-rules` from the palette opens
  // Automation around it. `defaultOpen` still wins where it is set explicitly.
  const location = useLocation();
  const holdsCurrentRoute = group.children.some(
    (child) => child.to === location.pathname || location.pathname.startsWith(`${child.to}/`),
  );
  const [open, setOpen] = useState(group.defaultOpen ?? holdsCurrentRoute);

  // Navigating into this group from elsewhere — the ⌘K palette, a link on a screen —
  // opens it. Only ever opens: a user who closed the group they are standing in should
  // not have it reopened under them on the next render.
  useEffect(() => {
    if (holdsCurrentRoute) setOpen(true);
  }, [holdsCurrentRoute]);

  // A collapsed rail has no room for a group header, so its children are
  // promoted to the rail directly — the icons are what the user navigates by
  // at 48px, and hiding half of them behind an unlabelled toggle would be
  // worse than losing the grouping.
  if (collapsed) {
    return (
      <>
        {group.children.map((child) => (
          <NavRow
            key={child.to}
            item={child}
            collapsed
            {...(child.badgeKey && counts?.[child.badgeKey] !== undefined
              ? { count: counts[child.badgeKey] }
              : {})}
          />
        ))}
      </>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`${ROW} ${ROW_IDLE}`}
      >
        <Icon name={group.icon} size={4} className="text-tertiary" />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <Icon
          name="chevron-right"
          size={4}
          className={`h-3.5 w-3.5 text-tertiary transition-transform duration-hover motion-reduce:transition-none ${
            open ? "rotate-90" : ""
          }`}
        />
      </button>
      {open ? (
        // The indent rail: 16px margin, a 1px divider, 8px of padding after
        // it. `border-weak` because this is a divider, not a control edge.
        <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-default pl-2">
          {group.children.map((child) => (
            <NavRow
              key={child.to}
              item={child}
              collapsed={false}
              {...(child.badgeKey && counts?.[child.badgeKey] !== undefined
                ? { count: counts[child.badgeKey] }
                : {})}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Which edges of the rail have content beyond them. */
interface ScrollEdges {
  top: boolean;
  bottom: boolean;
}

/**
 * The fade, as a mask, for whichever edges are overflowing.
 *
 * 20px is deliberately wider than a row is tall is not — a 36px row fading over 20px still
 * shows enough of its label to be read and clicked, while being visibly cut rather than
 * simply absent. A fade narrower than about 12px reads as a rendering seam.
 */
function edgeMask({ top, bottom }: ScrollEdges): string {
  const from = top ? "transparent 0, #000 20px" : "#000 0";
  const to = bottom ? "#000 calc(100% - 20px), transparent 100%" : "#000 100%";
  return `linear-gradient(to bottom, ${from}, ${to})`;
}

export function Sidebar({ collapsed, onToggleCollapsed, onOpenSearch, counts }: SidebarProps) {
  const navRef = useRef<HTMLElement | null>(null);
  const [edges, setEdges] = useState<ScrollEdges>({ top: false, bottom: false });

  /**
   * Recompute which edges overflow. Cheap enough to run on every scroll frame — two
   * layout reads and a `setState` that usually no-ops because the object compares equal
   * only when it changes, so React bails out of the re-render.
   */
  const updateEdges = useCallback(() => {
    const el = navRef.current;
    if (el === null) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setEdges((previous) =>
      previous.top === top && previous.bottom === bottom ? previous : { top, bottom },
    );
  }, []);

  // Before paint, so a rail that overflows on first render is never shown unfaded.
  useLayoutEffect(updateEdges, [updateEdges, collapsed]);

  // The rail's height changes with the window, and its *content* height changes when a
  // group is expanded — a `ResizeObserver` on the list catches both, where a `resize`
  // listener would catch only the first.
  useEffect(() => {
    const el = navRef.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(el);
    if (el.firstElementChild !== null) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [updateEdges]);

  // (H) `NAV`, not `NAV_BUILT`: the rail shows the whole product surface, with
  // the unbuilt destinations disabled rather than absent. `NAV_BUILT` is still
  // the right list for the ⌘K palette, where a destination you cannot open is
  // a result that wastes a keystroke.
  const rows: ReactNode = NAV.map((node) =>
    node.kind === "group" ? (
      <NavGroupRow key={node.label} group={node} collapsed={collapsed} counts={counts} />
    ) : (
      <NavRow
        key={node.to}
        item={node}
        collapsed={collapsed}
        {...(node.badgeKey && counts?.[node.badgeKey] !== undefined
          ? { count: counts[node.badgeKey] }
          : {})}
      />
    ),
  );

  return (
    <aside
      data-sidebar={collapsed ? "collapsed" : "expanded"}
      aria-label="Main navigation"
      className={[
        // doc 40 §8: 240px, 48px collapsed, white, right border-default.
        "z-sidebar flex shrink-0 flex-col border-r border-default bg-default",
        "transition-[width] duration-drawer-in ease-drawer-in motion-reduce:transition-none",
        collapsed ? "w-sidebar-collapsed" : "w-sidebar",
      ].join(" ")}
    >
      {/* The brand band is `h-topnav` so it aligns exactly with the top nav
          beside it — the two header bands read as one rule across the screen. */}
      <div
        className={[
          "flex h-topnav shrink-0 items-center border-b border-default",
          collapsed ? "justify-center px-2" : "gap-2 px-4",
        ].join(" ")}
      >
        {/* The real lockup, and the collapsed rail gets the mark alone — a
            wordmark scaled to fit 48px is unreadable, which is worse than
            showing only the glyph everyone recognises. */}
        <Brand variant={collapsed ? "mark" : "lockup"} className={collapsed ? "h-6" : "h-7"} />
      </div>

      {collapsed ? null : (
        <div className="shrink-0 px-2 pb-1 pt-3">
          {/* A button, not an input: the ⌘K palette is a dialog, and an input
              that steals focus on click then hands it to a dialog is two focus
              moves where one will do. */}
          <button
            type="button"
            onClick={onOpenSearch}
            className={[
              "flex w-full items-center gap-2 rounded-md border border-default bg-default px-3 py-1.5 text-sm",
              "text-tertiary hover:bg-active",
              "transition-colors duration-hover ease-hover motion-reduce:transition-none",
              "outline-none focus-visible:outline focus-visible:outline-2",
              "focus-visible:outline-offset-2 focus-visible:outline-strong",
            ].join(" ")}
          >
            <Icon name="search" size={4} className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="font-mono text-xs text-tertiary">⌘K</kbd>
          </button>
        </div>
      )}

      {/* ── The rail overflows on a 13" laptop, and it has to *look* like it ──────
          Measured with every group expanded: 22 rows, ~850px, against 559px of
          available height at 1280×720 — so 291px was hidden, and at 1440×789, 222px.
          It scrolled, so nothing was unreachable; it just looked like the nav ended
          at whatever row happened to be last on screen, with no hint of more.
          "Reports" and "Scoreboard" were the casualties, which is why `nav.ts` now
          orders by how often a destination is opened (D27).

          The mask is the affordance, and it is applied **only on the edges that have
          something beyond them** — a permanent fade would dim the first and last rows
          on a tall window where the rail fits perfectly, which is worse than no hint
          at all. A mask rather than a gradient overlay because it needs no extra
          element, cannot intercept a click on the row beneath it, and works on either
          theme without being told the background colour. `scrollbar-gutter: stable`
          keeps the rows from jumping sideways when the scrollbar appears. */}
      <nav
        ref={navRef}
        onScroll={updateEdges}
        className={`relative min-h-0 flex-1 overflow-y-auto py-2 [scrollbar-gutter:stable] ${
          // 4px each side collapsed, so the 40px square centres exactly in the 48px rail
          // (4 + 40 + 4). At `px-2` the row was 40px wide inside a 32px content box and
          // sat 8px left of centre with its right edge flush to the rail — measured.
          collapsed ? "px-1" : "px-2"
        }`}
        style={edges.top || edges.bottom ? { maskImage: edgeMask(edges), WebkitMaskImage: edgeMask(edges) } : undefined}
      >
        <ul className="space-y-0.5">{rows}</ul>
      </nav>

      <div className="shrink-0 border-t border-default p-2">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          className={`${collapsed ? ROW_COLLAPSED : ROW} ${ROW_IDLE} text-secondary`}
        >
          <Icon
            name="chevron-left"
            size={collapsed ? 5 : 4}
            className={`text-tertiary transition-transform duration-drawer-in motion-reduce:transition-none ${
              collapsed ? "rotate-180" : ""
            }`}
          />
          {collapsed ? null : <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}

export type { IconName };
